import React, { useCallback, useEffect, useState } from 'react';
import { t } from '@lingui/core/macro';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { MenuItem, Select } from '@mui/material';
import { getProjectRoleLabel, useProject } from '../contexts/ProjectContext';
import { auth } from '../firebase';
import { authFetch } from '../utils/authFetch';
import { getApiUrl } from '../utils/api';
import FontManagement, { FontFamilyInfo } from '../components/FontManagement';
import LogoManagement, { LogoInfo } from '../components/LogoManagement';
import { VideoSettingsPanel, VideoSettings } from '../components/VideoSettings';
import CardFramingPanel from '../components/CardFramingPanel';
import { CollapsibleSection } from '../components/CollapsibleSection';
import NavBar from '../components/NavBar';
import BackButton from '../components/BackButton';
import type { FontMetadata } from '../utils/fontHelpers';
import './ProjectSettings.css';

interface ProjectSettingsProps {
  fonts: FontFamilyInfo[];
  logos: LogoInfo[];
  videoSettings: VideoSettings;
  onFontListChange: (fonts: FontFamilyInfo[]) => void;
  onLogoListChange: (logos: LogoInfo[]) => void;
  onVideoSettingsChange: (settings: VideoSettings) => void;
  onFontUploaded?: (fontName: string, fontMetadata: FontMetadata) => void;
  onLogoUploaded?: (logoName: string, serverUrl: string) => void;
  onFontDeleted?: (fontName: string) => void;
  onLogoDeleted?: (logoName: string) => void;
}

interface ProjectMember {
  user_email: string;
  permission_tier: 1 | 2 | 3;
}

const sharingRoleValue = (permissionTier: ProjectMember['permission_tier']): string => {
  if (permissionTier === 1) return 'owner';
  if (permissionTier === 2) return 'editor';
  return 'data-only';
};

const SharingSection: React.FC<{ projectId: string }> = ({ projectId }) => {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [email, setEmail] = useState('');
  const [permissionTier, setPermissionTier] = useState<2 | 3>(2);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [pendingRemovalEmail, setPendingRemovalEmail] = useState<string | null>(null);
  const currentEmail = (auth.currentUser?.email || '').trim().toLowerCase();

  const loadMembers = useCallback(async (): Promise<void> => {
    try {
      const response = await authFetch(`${getApiUrl()}/projects/${projectId}/users`);
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || t`Failed to load shared users`);
      setMembers(result.users || []);
    } finally {
      setLoadingMembers(false);
    }
  }, [projectId]);

  useEffect(() => {
    void loadMembers().catch((loadError: unknown) => {
      setError(loadError instanceof Error ? loadError.message : String(t`Failed to load shared users`));
    });
  }, [loadMembers]);

  const addMember = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;
    setBusyEmail(normalizedEmail);
    setError('');
    setNotice('');
    try {
      const response = await authFetch(`${getApiUrl()}/projects/${projectId}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEmail: normalizedEmail, permissionTier }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || t`Failed to share project`);
      setEmail('');
      setNotice(result.warning || t`User added successfully`);
      await loadMembers();
    } catch (shareError) {
      setError(shareError instanceof Error ? shareError.message : String(t`Failed to share project`));
    } finally {
      setBusyEmail(null);
    }
  };

  const updateMember = async (memberEmail: string, nextTier: 2 | 3): Promise<void> => {
    setBusyEmail(memberEmail);
    setError('');
    setNotice('');
    try {
      const response = await authFetch(
        `${getApiUrl()}/projects/${projectId}/users/${encodeURIComponent(memberEmail)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ permissionTier: nextTier }),
        }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || t`Failed to update permission`);
      setNotice(result.warning || t`Permission updated`);
      await loadMembers();
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : String(t`Failed to update permission`));
    } finally {
      setBusyEmail(null);
    }
  };

  const removeMember = async (memberEmail: string): Promise<void> => {
    setBusyEmail(memberEmail);
    setError('');
    setNotice('');
    try {
      const response = await authFetch(
        `${getApiUrl()}/projects/${projectId}/users/${encodeURIComponent(memberEmail)}`,
        { method: 'DELETE' }
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || t`Failed to remove user`);
      setNotice(result.warning || t`User removed from project`);
      setPendingRemovalEmail(null);
      await loadMembers();
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : String(t`Failed to remove user`));
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <section className="sharing-section" aria-labelledby="project-sharing-title">
      <div className="sharing-header">
        <span className="sharing-header-icon" aria-hidden="true"><GroupOutlinedIcon /></span>
        <div>
          <h2 id="project-sharing-title">{t`Project Sharing`}</h2>
          <p>{t`Invite collaborators and choose what they can change.`}</p>
        </div>
      </div>

      <form className="sharing-add-form" onSubmit={addMember}>
        <label className="sharing-field sharing-email-field">
          <span>{t`Email address`}</span>
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@example.com"
            disabled={busyEmail !== null}
            required
          />
        </label>
        <label className="sharing-field">
          <span>{t`Role`}</span>
          <Select
            className="dropdown-menu-select"
            value={sharingRoleValue(permissionTier)}
            onChange={(event) => setPermissionTier(event.target.value === 'editor' ? 2 : 3)}
            disabled={busyEmail !== null}
            style={{ width: '130px', height: '34px' }}
          >
            <MenuItem value="editor">{t`Editor`}</MenuItem>
            <MenuItem value="data-only">{t`Data Only`}</MenuItem>
          </Select>
        </label>
        <button type="submit" className="sharing-add-button" disabled={busyEmail !== null || !email.trim()}>
          <PersonAddAltOutlinedIcon fontSize="small" />
          {busyEmail === email.trim().toLowerCase() ? t`Adding...` : t`Add User`}
        </button>
      </form>

      {error && <div className="sharing-message sharing-message-error" role="alert">{error}</div>}
      {notice && <div className="sharing-message sharing-message-success" role="status">{notice}</div>}

      <div className="sharing-list-heading">
        <span>{t`People with access`}</span>
        {!loadingMembers && <span className="sharing-count">{members.length}</span>}
      </div>
      <div className="sharing-members" aria-live="polite">
        {loadingMembers && <div className="sharing-empty">{t`Loading shared users...`}</div>}
        {!loadingMembers && members.length === 0 && (
          <div className="sharing-empty">{t`No one else has access yet.`}</div>
        )}
        {!loadingMembers && members.map((member) => {
          const isSelf = member.user_email === currentEmail;
          const isBusy = busyEmail === member.user_email;
          const isConfirmingRemoval = pendingRemovalEmail === member.user_email;
          return (
            <div className="sharing-member" key={member.user_email}>
              <div className="sharing-member-identity">
                <span className="sharing-avatar" aria-hidden="true">{member.user_email.charAt(0).toUpperCase()}</span>
                <div>
                  <span className="sharing-member-email">{member.user_email}</span>
                  {isSelf && <span className="sharing-you-label">{t`You`}</span>}
                </div>
              </div>
              <Select
                className="dropdown-menu-select"
                aria-label={t`Role for ${member.user_email}`}
                value={sharingRoleValue(member.permission_tier)}
                disabled={isSelf || isBusy}
                onChange={(event) => void updateMember(member.user_email, event.target.value === 'editor' ? 2 : 3)}
                style={{ width: '130px', height: '34px' }}
              >
                {member.permission_tier === 1 && <MenuItem value="owner">{t`Owner`}</MenuItem>}
                <MenuItem value="editor">{t`Editor`}</MenuItem>
                <MenuItem value="data-only">{t`Data Only`}</MenuItem>
              </Select>
              <div className="sharing-member-actions">
                {isConfirmingRemoval ? (
                  <>
                    <button type="button" className="sharing-cancel-button" onClick={() => setPendingRemovalEmail(null)} disabled={isBusy}>
                      {t`Cancel`}
                    </button>
                    <button type="button" className="sharing-confirm-remove-button" onClick={() => void removeMember(member.user_email)} disabled={isBusy}>
                      {isBusy ? t`Removing...` : t`Confirm`}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="sharing-remove-button"
                    aria-label={t`Remove ${member.user_email}`}
                    disabled={isSelf || isBusy}
                    onClick={() => setPendingRemovalEmail(member.user_email)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                    {t`Remove`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  fonts,
  logos,
  videoSettings,
  onFontListChange,
  onLogoListChange,
  onVideoSettingsChange,
  onFontUploaded,
  onLogoUploaded,
  onFontDeleted,
  onLogoDeleted,
}) => {
  const { selectedProject } = useProject();

  if (!selectedProject) {
    return null;
  }
  const canEdit = selectedProject.permission_tier === 1 || selectedProject.permission_tier === 2;

  return (
    <div className="project-settings-page">
      <NavBar />
      <div className="project-settings-content">
        <BackButton spacing />

        <div className="project-role-summary">
          {t`Your role`}: {getProjectRoleLabel(selectedProject.permission_tier)}
        </div>

        <fieldset className="settings-readonly-boundary" disabled={!canEdit}>
        <div className="settings-sections">
          <div className="settings-primary-column">
            <div className="video-settings-section">
              <CollapsibleSection title={String(t`Video Settings`)} contentClassName="video-settings-section-body">
                <VideoSettingsPanel
                  settings={videoSettings}
                  onSettingsChange={onVideoSettingsChange}
                  showTitle={false}
                  showBackgroundControls={videoSettings.mode !== 'cards'}
                />
              </CollapsibleSection>
            </div>
            {videoSettings.mode === 'cards' && (
              <div className="video-settings-section">
                <CollapsibleSection title={String(t`Card Framing`)} contentClassName="video-settings-section-body">
                  <CardFramingPanel
                    settings={videoSettings}
                    onSettingsChange={onVideoSettingsChange}
                  />
                </CollapsibleSection>
              </div>
            )}
          </div>
          <div className="settings-management-column">
            <FontManagement
              onFontListChange={onFontListChange}
              initialFonts={fonts}
              onFontUploaded={onFontUploaded}
              onFontDeleted={onFontDeleted}
            />

            <LogoManagement
              onLogoListChange={onLogoListChange}
              initialLogos={logos}
              onLogoUploaded={onLogoUploaded}
              onLogoDeleted={onLogoDeleted}
            />
          </div>
        </div>
        </fieldset>
        {selectedProject.permission_tier === 1 && <SharingSection projectId={selectedProject.id} />}
      </div>
    </div>
  );
};

export default ProjectSettings;
